import { useState } from "react";
import { toast } from "sonner";
import { History, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const OLD_SITE = "https://blenny-buttercup-achs.squarespace.com";
const LABEL = { import: "Will import", imported: "Imported", already_imported: "Already imported", skip_active: "Skipped — active",
  conflict: "Conflict — skipped", flag_unmatched: "Needs review — no match" };

function Rows({ rows }) {
  return (
    <ul data-testid="historical-import-rows" className="mt-4 divide-y divide-stone-100 text-sm">
      {rows.map((r) => (
        <li key={r.squarespace_id} data-testid={`historical-row-${r.squarespace_id}`} className="py-2 flex flex-wrap gap-x-3 gap-y-0.5">
          <span className="font-semibold text-stone-900">{r.title}</span>
          {r.barn_name && <span className="text-stone-600">→ {r.barn_name}{r.is_test ? " (TEST)" : ""}</span>}
          <span className={`text-xs font-semibold ${r.action === "flag_unmatched" || r.action === "conflict" ? "text-amber-800" : "text-stone-500"}`}>{LABEL[r.action] || r.action}</span>
          {[r.reason, ...(r.notes || [])].filter(Boolean).map((n) => <span key={n} className="basis-full text-xs text-stone-500">{n}</span>)}
        </li>
      ))}
    </ul>
  );
}

export function HistoricalImport() {
  const { user } = useAuth();
  const [site, setSite] = useState(OLD_SITE);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  if (user?.role !== "super_admin") return null;
  const run = async (kind) => {
    if (kind === "apply" && !window.confirm(`Import ${res?.counts?.import || 0} historical ads onto existing horses? Existing ads, sale outcomes and statuses are not changed.`)) return;
    setBusy(kind);
    try {
      const { data } = await api.post(`/admin/historical-import/${kind}`, { site_url: site });
      setRes(data);
      if (kind === "apply") toast.success(`${data.counts.imported || 0} historical ads imported`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read the old site"); }
    setBusy("");
  };
  return (
    <section data-testid="historical-import" className="rounded-xl border border-stone-200 bg-white p-5">
      <h2 className="font-semibold text-stone-900 mb-1 flex items-center gap-2"><History className="h-4 w-4" />Historical Ads Import (Squarespace)</h2>
      <p className="text-xs text-stone-500 mb-4">Adds a preserved copy of each old-site ad to its matching horse and lists it under Completed. Never publishes, never changes sale outcomes or TEST/LIVE. Unmatched ads are only flagged.</p>
      <Input data-testid="historical-import-site" value={site} onChange={(e) => setSite(e.target.value)} className="mb-3 border-stone-200" />
      <div className="flex flex-wrap gap-2">
        <Button data-testid="historical-import-preview" variant="outline" disabled={!!busy} onClick={() => run("preview")} className="rounded-full">
          {busy === "preview" && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Preview</Button>
        <Button data-testid="historical-import-apply" disabled={!!busy || !res?.counts?.import} onClick={() => run("apply")} className="rounded-full bg-amber-800 hover:bg-amber-900 text-white">
          {busy === "apply" && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Import {res?.counts?.import || 0}</Button>
      </div>
      {res && <p data-testid="historical-import-counts" className="mt-3 text-xs text-stone-600">{res.total} ads · {Object.entries(res.counts).map(([k, v]) => `${LABEL[k] || k}: ${v}`).join(" · ")}</p>}
      {res && <Rows rows={res.rows} />}
    </section>
  );
}
